/* @jsx h */
import { useNativeBridge } from "@app/vue/providers/NativeBridge";
import { useLocalization } from "@lemmonhq/uikit";
import { defineComponent, h } from "vue";

const VIDEO_ID = "lOD9Q11z_sE";
const VIDEO_URL = `https://www.youtube.com/watch?v=${VIDEO_ID}`;
const THUMBNAIL_URL = `https://img.youtube.com/vi/${VIDEO_ID}/hqdefault.jpg`;

// Epson TM-m30III: print the status sheet, then the Bluetooth status sheet,
// which leaves the printer in pairing mode for a minute.
const PAIRING_STEPS = [
  "pairing_step_cover",
  "pairing_step_feed",
  "pairing_step_close",
  "pairing_step_bluetooth",
];

export const PairingVideo = defineComponent({
  name: "PrinterPairingVideo",
  setup() {
    const { t } = useLocalization();
    const bridge = useNativeBridge();

    // In the app the OS opens the YouTube app when it is installed.
    const open = async () => {
      if (bridge) {
        try {
          await bridge.rpc.request("openUrl", { url: VIDEO_URL });
          return;
        } catch (error) {
          console.error("Failed to open video through native bridge:", error);
        }
      }
      window.open(VIDEO_URL, "_blank", "noopener");
    };

    return () => (
      <button
        type="button"
        onClick={open}
        class="group relative mb-4 block w-44 overflow-hidden rounded-xl shadow-lg ring-1 ring-base-content/10"
      >
        <img
          src={THUMBNAIL_URL}
          alt={t("devices.bt_printer.pairing_video")}
          loading="lazy"
          class="aspect-video w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div class="absolute inset-0 bg-gradient-to-t from-base-300/90 via-base-300/20 to-transparent" />
        <div class="absolute inset-0 flex items-center justify-center">
          <span class="flex h-8 w-8 items-center justify-center rounded-full bg-error text-error-content shadow-lg">
            <span class="material-symbols-outlined text-xl">play_arrow</span>
          </span>
        </div>
        <span class="absolute bottom-2 left-3 right-3 truncate text-left text-xs font-semibold text-accent">
          {t("devices.bt_printer.pairing_video")}
        </span>
      </button>
    );
  },
});

/** How to put the printer in Bluetooth pairing mode before searching. */
export const PairingGuide = defineComponent({
  name: "PrinterPairingGuide",
  setup() {
    const { t } = useLocalization();
    return () => (
      <div class="w-full max-w-sm text-left">
        <div class="rounded-xl bg-base-200 p-3.5 ring-1 ring-base-content/5">
          <p class="mb-2.5 text-xs font-semibold uppercase tracking-wider text-base-content/50">
            {t("devices.bt_printer.pairing_title")}
          </p>
          <ol class="space-y-2.5">
            {PAIRING_STEPS.map((key, index) => (
              <li key={key} class="flex gap-3">
                <span class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                  {index + 1}
                </span>
                <span class="pt-0.5 text-sm leading-snug text-base-content/80">
                  {t(`devices.bt_printer.${key}`)}
                </span>
              </li>
            ))}
          </ol>
          <p class="mt-3 flex gap-2 text-xs text-base-content/50">
            <span class="material-symbols-outlined text-base">timer</span>
            {t("devices.bt_printer.pairing_timeout")}
          </p>
        </div>
      </div>
    );
  },
});
